<template>
  <div>
    <!-- Barra del buffet activo -->
    <div class="buffet-bar">
      <div class="buffet-text">
        <span class="eyebrow">{{ ui.buffetEyebrow }}</span>
        <span class="buffet-name">{{ categoriaName }}</span>
      </div>
    </div>

    <!-- Selector de día -->
    <div class="section">
      <div class="eyebrow">{{ ui.diaLabel }}</div>
      <div data-rail class="rail" @scroll="updateRailFades">
        <button
          v-for="n in 7"
          :key="n"
          class="day"
          :class="{ active: n === store.dia, today: n === today && n !== store.dia }"
          @click="setDia(n)"
        >
          <span class="day-label">{{ dias[n - 1] }}</span>
          <span v-if="n === today" class="day-today">{{ ui.hoy }}</span>
        </button>
      </div>
    </div>

    <!-- Filtro por familia -->
    <div v-if="store.familias.length" class="section">
      <div class="eyebrow">{{ ui.familiaLabel }}</div>
      <div data-rail class="rail" @scroll="updateRailFades">
        <button
          class="cat"
          :class="{ active: store.selectedFamilia === null }"
          @click="setFamilia(null)"
        >{{ ui.todas }}</button>
        <button
          v-for="f in store.familias"
          :key="f.id"
          class="cat"
          :class="{ active: store.selectedFamilia === f.id }"
          @click="setFamilia(f.id)"
        >{{ f.nombre }}</button>
      </div>
    </div>

    <!-- Búsqueda + filtro -->
    <div class="controls">
      <div class="search">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--ink-500)" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><circle cx="11" cy="11" r="7" /><path d="m20 20-3.2-3.2" /></svg>
        <input
          :value="store.query"
          :placeholder="ui.search"
          @input="setQuery($event.target.value)"
        />
        <button v-if="store.query.trim()" class="clear" @click="clearQuery">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"><path d="M18 6 6 18M6 6l12 12" /></svg>
        </button>
      </div>
      <div v-if="store.mine.length" class="filter">
        <switch-toggle :checked="store.onlyApto" @change="setOnlyApto" />
        <span>{{ ui.filter }}</span>
      </div>
    </div>

    <!-- Resumen + leyenda -->
    <div class="summary-row">
      <div class="summary">{{ summaryText }}</div>
      <div class="legend">
        <span class="legend-item"><span class="swatch contiene" />{{ ui.contiene }}</span>
        <span class="legend-item"><span class="swatch traza" />{{ ui.trazasT }}</span>
      </div>
    </div>

    <!-- Estados -->
    <div v-if="store.loading" class="state">{{ ui.cargando }}</div>

    <div v-else-if="store.error" class="state">
      <div class="state-title">{{ ui.errorCarga }}</div>
      <app-button variant="secondary" pill class="retry" @click="retry">{{ ui.reintentar }}</app-button>
    </div>

    <div v-else-if="dishes.length" class="grid">
      <dish-card v-for="d in dishes" :key="d.id" :dish="d" />
    </div>

    <div v-else class="empty">
      <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="var(--sand-400)" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"><path d="M3 11h18M5 11V7a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v4M5 11v6a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-6" /></svg>
      <div class="empty-title">{{ ui.sinResultados }}</div>
      <div class="empty-sub">{{ ui.sinResultadosSub }}</div>
    </div>
  </div>
</template>

<script>
import { UI, DIAS, isoToday } from '@/data'
import { dishStatus } from '@/viewmodel'
import { store, setDia, setFamilia, setQuery, clearQuery, setOnlyApto, navigateLanding, retry } from '@/store'
import DishCard from './DishCard.vue'
import AppButton from './ui/AppButton.vue'
import SwitchToggle from './ui/SwitchToggle.vue'

export default {
  name: 'MenuView',
  components: { DishCard, AppButton, SwitchToggle },
  data() {
    return { store, today: isoToday() }
  },
  computed: {
    ui() { return UI[store.locale] || UI.es },
    dias() { return DIAS[store.locale] || DIAS.es },
    categoriaName() {
      return store.categoria ? store.categoria.nombre : ''
    },
    pool() {
      let list = store.escandalls
      if (store.selectedFamilia != null) {
        list = list.filter(d => (d.familia_ids || []).includes(store.selectedFamilia))
      }
      const q = store.query.trim().toLowerCase()
      if (q) list = list.filter(d => (d.nombre || '').toLowerCase().includes(q))
      return list
    },
    dishes() {
      if (store.onlyApto && store.mine.length) {
        return this.pool.filter(d => dishStatus(d.alergeno_ids || [], d.traza_ids || [], store.mine) !== 'danger')
      }
      return this.pool
    },
    summaryText() {
      const dayName = this.dias[store.dia - 1]
      const prefix = `${this.categoriaName} · ${dayName} — `
      if (store.mine.length) {
        const total = this.pool.length
        const apto = this.pool.filter(d => dishStatus(d.alergeno_ids || [], d.traza_ids || [], store.mine) !== 'danger').length
        return `${prefix}${apto}/${total} ${this.ui.aptos}`
      }
      const n = this.dishes.length
      return `${prefix}${n} ${n === 1 ? this.ui.plato : this.ui.platos}`
    }
  },
  mounted() {
    this.updateRailFades()
    window.addEventListener('resize', this.updateRailFades)
  },
  updated() {
    this.$nextTick(this.updateRailFades)
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.updateRailFades)
  },
  methods: {
    setDia, setFamilia, setQuery, clearQuery, setOnlyApto, navigateLanding, retry,
    // Activa el difuminado lateral solo en el lado que tiene contenido oculto.
    updateRailFades() {
      this.$el.querySelectorAll('[data-rail]').forEach(rail => {
        const max = rail.scrollWidth - rail.clientWidth
        rail.classList.toggle('fade-start', rail.scrollLeft > 1)
        rail.classList.toggle('fade-end', rail.scrollLeft < max - 1)
      })
    }
  }
}
</script>

<style scoped>
.buffet-bar {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 18px;
}
.back {
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: 1px solid var(--sand-300);
  background: #fff;
  color: var(--seth-navy);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 180ms;
}
.back:hover { background: var(--seth-navy); color: #fff; border-color: var(--seth-navy); }
.buffet-text { display: flex; flex-direction: column; min-width: 0; }
.buffet-name {
  font-family: var(--font-display);
  font-weight: 300;
  font-size: 1.5rem;
  color: var(--seth-navy);
  line-height: 1.1;
}

.section { margin-bottom: 18px; }
.eyebrow {
  font-size: 0.7rem;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: var(--seth-gold-600);
  font-weight: 600;
  margin-bottom: 8px;
}
.rail {
  display: flex;
  gap: 8px;
  overflow-x: auto;
  padding-bottom: 4px;
  /* Difuminado lateral: ancho 0 por defecto, lo activan .fade-start/.fade-end */
  --fade-l: 0px;
  --fade-r: 0px;
  -webkit-mask-image: linear-gradient(to right, transparent, #000 var(--fade-l), #000 calc(100% - var(--fade-r)), transparent);
  mask-image: linear-gradient(to right, transparent, #000 var(--fade-l), #000 calc(100% - var(--fade-r)), transparent);
}
.rail.fade-start { --fade-l: 28px; }
.rail.fade-end { --fade-r: 28px; }

.day {
  flex-shrink: 0;
  min-width: 64px;
  min-height: 60px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1px;
  padding: 8px 14px;
  border-radius: 14px;
  cursor: pointer;
  transition: all 200ms;
  background: #fff;
  color: var(--ink-700);
  border: 1px solid var(--sand-200);
}
.day-label { font-size: 0.95rem; font-weight: 700; }
.day-today {
  font-size: 0.6rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  margin-top: 2px;
  color: var(--seth-gold-600);
}
.day.today { background: #F6EFD9; color: #9A7B16; border: 1.5px solid #C4A02A; }
.day.today .day-today { color: var(--seth-gold-600); }
.day.active { background: var(--seth-navy); color: #fff; border: 1.5px solid var(--seth-navy); }
.day.active .day-today { color: #C4A02A; }

.cat {
  flex-shrink: 0;
  padding: 10px 18px;
  border-radius: 999px;
  cursor: pointer;
  font-family: var(--font-sans);
  font-weight: 600;
  font-size: 0.9rem;
  white-space: nowrap;
  transition: all 200ms;
  background: #fff;
  color: var(--ink-700);
  border: 1px solid var(--sand-200);
}
.cat.active { background: var(--seth-navy); color: #fff; border-color: var(--seth-navy); }

.controls {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  align-items: center;
  margin-bottom: 14px;
}
.search {
  flex: 1;
  min-width: 220px;
  display: flex;
  align-items: center;
  gap: 10px;
  background: #fff;
  border: 1px solid var(--sand-300);
  border-radius: var(--radius-pill);
  padding: 0 16px;
  height: 46px;
}
.search input {
  flex: 1;
  border: none;
  outline: none;
  background: transparent;
  font-family: var(--font-sans);
  font-size: 0.95rem;
  color: var(--ink-900);
  height: 100%;
}
.clear {
  border: none;
  background: transparent;
  cursor: pointer;
  color: var(--ink-500);
  display: flex;
  padding: 4px;
}
.filter {
  display: flex;
  align-items: center;
  gap: 10px;
  background: #fff;
  border: 1px solid var(--sand-300);
  border-radius: var(--radius-pill);
  padding: 8px 16px;
  height: 46px;
}
.filter span { font-size: 0.9rem; font-weight: 600; color: var(--ink-900); white-space: nowrap; }

.summary-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 14px;
}
.summary { font-size: 0.9rem; color: var(--ink-700); }
.legend { display: flex; gap: 14px; align-items: center; font-size: 0.74rem; color: var(--ink-500); }
.legend-item { display: inline-flex; align-items: center; gap: 5px; }
.swatch { width: 11px; height: 11px; border-radius: 3px; }
.swatch.contiene { background: var(--sand-100); border: 1px solid var(--sand-200); }
.swatch.traza { border: 1px dashed var(--sand-400); }

.grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 14px;
}

.state { text-align: center; padding: 48px 20px; color: var(--ink-500); }
.state-title { font-size: 0.95rem; margin-bottom: 14px; }
.retry { margin: 0 auto; }

.empty { text-align: center; padding: 64px 20px; color: var(--ink-500); }
.empty svg { margin-bottom: 12px; }
.empty-title { font-family: var(--font-display); font-size: 1.15rem; color: var(--seth-navy); margin-bottom: 6px; }
.empty-sub { font-size: 0.9rem; }
</style>
